// Toast Notification Store - Pinia Setup Store Pattern
// Provides global toast messaging functionality

import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ToastMessage {
  severity: 'success' | 'info' | 'warn' | 'error';
  summary: string;
  detail?: string;
  life?: number;
}

export const useToastStore = defineStore('toast', () => {
  // State
  const toastRef = ref<any>(null);

  /**
   * Set toast component reference (called from App.vue)
   */
  const setToastRef = (ref: any): void => {
    toastRef.value = ref;
  };

  /**
   * Show a toast message
   */
  const showToast = (message: ToastMessage): void => {
    if (toastRef.value) {
      toastRef.value.add({
        severity: message.severity,
        summary: message.summary,
        detail: message.detail || '',
        life: message.life || 3000
      });
    }
  };

  /**
   * Show success toast
   */
  const setToastSuccessMessage = (message: string, detail?: string): void => {
    showToast({
      severity: 'success',
      summary: message,
      detail,
      life: 3000
    });
  };

  /**
   * Show error toast
   */
  const setToastErrorMessage = (message: string, detail?: string): void => {
    showToast({
      severity: 'error',
      summary: message,
      detail,
      life: 5000
    });
  };

  /**
   * Show warning toast
   */
  const setToastWarningMessage = (message: string, detail?: string): void => {
    showToast({
      severity: 'warn',
      summary: message,
      detail,
      life: 4000
    });
  };

  /**
   * Show info toast
   */
  const setToastInfoMessage = (message: string, detail?: string): void => {
    showToast({
      severity: 'info',
      summary: message,
      detail,
      life: 3000
    });
  };

  return {
    // State
    toastRef,
    // Actions
    setToastRef,
    showToast,
    setToastSuccessMessage,
    setToastErrorMessage,
    setToastWarningMessage,
    setToastInfoMessage
  };
});
